import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../services/api";
import {
  DeviceSummary,
  DeviceDetail,
  BundleSummary,
  TenantSummary,
  EnrollTokenResponse,
} from "../types";

export const useDevices = (tenantId?: string) => {
  return useQuery<DeviceSummary[], Error>({
    queryKey: ["devices", tenantId],
    queryFn: () => api.listDevices(tenantId),
    staleTime: 30000,
    refetchInterval: 30000,
  });
};

export const useDevice = (id: string) => {
  return useQuery<DeviceDetail, Error>({
    queryKey: ["device", id],
    queryFn: () => api.getDevice(id),
    enabled: !!id,
    staleTime: 30000,
    refetchInterval: 30000,
  });
};

export const useDeviceBundles = (id: string) => {
  return useQuery<BundleSummary[], Error>({
    queryKey: ["device-bundles", id],
    queryFn: () => api.getDeviceBundles(id),
    enabled: !!id,
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

export const useTenants = () => {
  return useQuery<TenantSummary[], Error>({
    queryKey: ["tenants"],
    queryFn: () => api.listTenants(),
    staleTime: 60000,
  });
};

export const useCreateEnrollToken = () => {
  const queryClient = useQueryClient();
  return useMutation<EnrollTokenResponse, Error, { name: string; role: string }>({
    mutationFn: ({ name, role }) => api.createEnrollToken(name, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
};

export const useKillDevice = () => {
  const queryClient = useQueryClient();
  return useMutation<{}, Error, { deviceId: string; reason: string }>({
    mutationFn: ({ deviceId, reason }) => api.killDevice(deviceId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["device-bundles"] });
    },
  });
};